import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { FIELD_GROUPS, PROFILE_FIELDS, submissionSchema, computeAge } from '@accheyrishtey/shared';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/ui/Button';
import { Input, Select, Textarea } from '../components/ui/Field';
import { Logo } from '../components/ui/Logo';
import { Icon } from '../components/ui/Icon';

interface Signature {
  signature: string; timestamp: number; api_key: string; cloud_name: string;
  folder: string; upload_url: string;
}
interface UploadedPhoto {
  public_id: string; url: string; width: number; height: number; bytes: number;
}

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
const countries = getCountries().map((code) => ({
  code,
  label: `${countryNames.of(code) ?? code} (+${getCountryCallingCode(code)})`,
})).sort((a, b) => a.label.localeCompare(b.label));

const defaultValues: Record<string, unknown> = {
  contact_country: 'IN', father_contact_country: 'IN', mother_contact_country: 'IN', consent: false,
};

const REQUIRED_FIELD_KEYS = new Set([
  'full_name', 'contact_number', 'date_of_birth', 'gender', 'marital_status',
]);

function countryKeyFor(phoneKey: string): string {
  return phoneKey === 'contact_number' ? 'contact_country'
    : phoneKey === 'father_contact_number' ? 'father_contact_country' : 'mother_contact_country';
}

function FormControl({ field, value, onChange }: {
  field: (typeof PROFILE_FIELDS)[number]; value: unknown; onChange: (key: string, value: unknown) => void;
}) {
  const common = { id: field.key, name: field.key, value: String(value ?? ''), onChange: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => onChange(field.key, e.target.value) };
  if (field.input === 'textarea') return <Textarea {...common} rows={4} />;
  if (field.input === 'select') return <Select {...common}><option value="">Select an option</option>{field.options?.map((option) => <option key={option}>{option}</option>)}</Select>;
  return <Input {...common} type={field.input === 'number' || field.input === 'height' ? 'number' : field.input === 'date' ? 'date' : 'text'} min={field.input === 'height' ? 120 : undefined} max={field.input === 'height' ? 220 : undefined} />;
}

export function PublicFormPage() {
  const { token = '' } = useParams();
  const storageKey = `ar-form-${token}`;
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(1);
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    try { return { ...defaultValues, ...JSON.parse(localStorage.getItem(storageKey) ?? '{}') }; }
    catch { return { ...defaultValues }; }
  });
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'checking' | 'ready' | 'inactive' | 'sending' | 'done'>('checking');

  useEffect(() => {
    const previous = document.title;
    document.title = 'AccheyRishtey — Biodata Form';
    return () => { document.title = previous; };
  }, []);
  useEffect(() => {
    api.get(`/public/form/${token}`).then(() => setStatus('ready')).catch(() => setStatus('inactive'));
  }, [token]);
  useEffect(() => { if (status === 'ready') localStorage.setItem(storageKey, JSON.stringify(values)); }, [status, storageKey, values]);

  const stepFields = useMemo(() => {
    if (step === 4) return PROFILE_FIELDS.filter((field) => field.group === 'address' || field.group === 'preferences');
    const group = FIELD_GROUPS.find((item) => item.step === step)?.key;
    return PROFILE_FIELDS.filter((field) => field.group === group);
  }, [step]);
  const setValue = (key: string, value: unknown) => setValues((current) => ({ ...current, [key]: value }));
  const validateStep = () => {
    if (step === 1) {
      for (const key of ['full_name', 'contact_number', 'date_of_birth', 'gender', 'marital_status']) {
        if (!String(values[key] ?? '').trim()) { setError('Please complete all required personal details.'); return false; }
      }
    }
    if (step === 5 && !values.consent) { setError('Please agree to the consent statement before submitting.'); return false; }
    setError(''); return true;
  };
  const move = (next: number) => { if (next > step && !validateStep()) return; setDirection(next > step ? 1 : -1); setStep(next); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const uploadPhoto = async (file: File): Promise<UploadedPhoto> => {
    const signature = await api.post<Signature>(`/public/form/${token}/signature`);
    const data = new FormData();
    data.append('file', file); data.append('api_key', signature.api_key); data.append('timestamp', String(signature.timestamp)); data.append('signature', signature.signature); data.append('folder', signature.folder);
    const response = await fetch(signature.upload_url, { method: 'POST', body: data });
    if (!response.ok) throw new Error('Photograph upload failed');
    const uploaded = await response.json() as { public_id: string; secure_url: string; width: number; height: number; bytes: number };
    return { public_id: uploaded.public_id, url: uploaded.secure_url, width: uploaded.width, height: uploaded.height, bytes: uploaded.bytes };
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault(); if (!validateStep()) return; setStatus('sending'); setError('');
    try {
      const photos = await Promise.all(files.map(uploadPhoto));
      const body = Object.fromEntries(Object.entries(values).filter(([, value]) => value !== '' && value !== undefined));
      const parsed = submissionSchema.safeParse({ ...body, photos, website: '' });
      if (!parsed.success) {
        const first = parsed.error.issues[0]?.message ?? 'Please check the form.';
        throw new Error(first);
      }
      await api.post(`/public/form/${token}/submit`, parsed.data);
      localStorage.removeItem(storageKey); setStatus('done');
    } catch (reason) {
      setStatus('ready'); setError(reason instanceof ApiError || reason instanceof Error ? reason.message : 'Could not submit the form.');
    }
  };

  if (status === 'checking') return <div className="page-centre"><div className="spinner spinner--large"/><p>Opening your form…</p></div>;
  if (status === 'inactive') return <main className="public-form-shell public-form-shell--message"><Logo/><div className="empty-state__arch">AR</div><h1>This form link is no longer active</h1><p>Please ask for a current link.</p></main>;
  if (status === 'done') return <main className="public-form-shell public-form-shell--message"><Logo/><div className="success-mark">✓</div><h1>Thank you</h1><p>Your details have been submitted successfully. The AccheyRishtey administrator will take it from here.</p></main>;

  return <main className="public-form-shell">
    <header className="public-form-head"><Logo compact/><div><strong>Step {step} of 5</strong><span>{['Personal details','Education & work','Family','Address & preferences','Photographs & consent'][step - 1]}</span></div></header>
    <section className="public-form-intro" aria-labelledby="public-form-title">
      <p className="eyebrow">Free community rishta service</p>
      <h1 id="public-form-title">Acchey Rishtey - Share Your Profile</h1>
      <blockquote>
        <p>&quot;And among His signs is that He created for you, from among yourselves, mates - so that you might find rest and comfort in them - and He placed between you love and mercy. Truly in this are signs for people who reflect.&quot;</p>
        <cite>- Qur&apos;an, Surah Ar-Rum 30:21 (rendered in meaning)</cite>
      </blockquote>
      <p>Acchey Rishtey is a free, community-run rishta service. Please fill this in as completely and honestly as you can - it takes about 10 minutes. Fields marked <strong>&quot;Required&quot;</strong> must be filled in; everything else is optional.</p>
    </section>
    <div className="progress-rail">{[1,2,3,4,5].map((item) => <span className={item <= step ? 'is-active' : ''} key={item}/>)}</div>
    <form onSubmit={submit}>
      <AnimatePresence mode="wait" custom={direction}>
        <motion.section key={step} custom={direction} initial={{ opacity: 0, x: direction * 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: direction * -24 }} className="form-step">
          <div className="form-step__title"><span>0{step}</span><div><h1>{['Tell us about yourself','Education and work','Your family','Address and preferences','Photographs and consent'][step - 1]}</h1><p>Required fields are marked. You can return to earlier steps at any time.</p></div></div>
          {step < 5 ? <div className="form-grid">{stepFields.map((field) => <label className={field.input === 'textarea' ? 'field field--wide' : 'field'} key={field.key} htmlFor={field.key}><span className="public-field-label">{field.label}{REQUIRED_FIELD_KEYS.has(field.key) ? <span className="required-badge">Required</span> : null}</span>
            {field.input === 'phone' ? <div className="phone-split"><Select value={String(values[countryKeyFor(field.key)] ?? 'IN')} onChange={(e) => setValue(countryKeyFor(field.key), e.target.value)} aria-label={`${field.label} country`}>{countries.map((country) => <option key={country.code} value={country.code}>{country.label}</option>)}</Select><Input id={field.key} value={String(values[field.key] ?? '')} onChange={(e) => setValue(field.key, e.target.value)} type="tel" inputMode="tel"/></div>
              : <FormControl field={field} value={values[field.key]} onChange={setValue}/>}
            {field.key === 'date_of_birth' && values.date_of_birth ? <small>Age: {computeAge(String(values.date_of_birth))} years</small> : null}
          </label>)}</div> : <div className="photo-step">
            <label className="photo-drop"><Icon name="camera"/><strong>Add up to six photographs</strong><span>JPG, PNG or WebP · maximum 5 MB each</span><input type="file" accept="image/*" multiple onChange={(e) => setFiles(Array.from(e.target.files ?? []).slice(0, 6))}/></label>
            {files.length ? <div className="photo-previews">{files.map((file, index) => <figure key={`${file.name}-${index}`}><img src={URL.createObjectURL(file)} alt=""/><button type="button" onClick={() => setFiles((current) => current.filter((_, item) => item !== index))}>Remove</button></figure>)}</div> : null}
            <label className="consent-box"><input type="checkbox" checked={Boolean(values.consent)} onChange={(e) => setValue('consent', e.target.checked)}/><span><strong>I consent to these details and selected photographs being stored and shared by the Acchey Rishtey administrator for matrimonial matching.</strong><small>Only the administrator can access the dashboard. Information shared with a prospective match may be saved or forwarded by the recipient.</small></span></label>
          </div>}
        </motion.section>
      </AnimatePresence>
      {error ? <div className="alert alert--error" role="alert">{error}</div> : null}
      <footer className="form-nav">{step > 1 ? <Button type="button" variant="ghost" onClick={() => move(step - 1)}>Back</Button> : <span/>}{step < 5 ? <Button type="button" onClick={() => move(step + 1)}>Continue</Button> : <Button type="submit" loading={status === 'sending'}>Submit profile</Button>}</footer>
    </form>
  </main>;
}
